import { ReactNode, useMemo, useRef } from "react";
import { Column, usePagination, useSortBy, useTable } from "react-table";

import { LoadingOverlay } from "./LoadingComponents";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadContent,
  TableHeader,
  TableRow,
} from "./Table";
import Pagination from "./pagination/Pagination";

export default function DefaultTable<T extends object = any>({
  columns,
  data,
  empty,
  onRowClick,
  paginate = false,
  isLoading,
  isSortable,
}: {
  columns: Column<T>[];
  data: T[];
  empty?: ReactNode;
  onRowClick?: (row: T) => void;
  paginate?: boolean;
  isLoading?: boolean;
  isSortable?: boolean;
}) {
  const {
    canNextPage,
    canPreviousPage,
    getTableBodyProps,
    getTableProps,
    gotoPage,
    headerGroups,
    page,
    pageCount,
    prepareRow,
    rows,
    state: { pageIndex },
  } = useTable<T>(
    {
      columns,
      data,
      initialState: {
        pageSize: 10,
      },
    },
    ...(useRef(isSortable).current ? [useSortBy] : []),
    usePagination,
  );

  const shownRows = useMemo(
    () => (paginate ? page : rows),
    [paginate, page, rows],
  );

  return (
    <div className="relative flex w-full flex-col">
      <div className="w-full">
        <Table {...getTableProps()}>
          <TableHeader>
            {headerGroups.map((headerGroup) => (
              <TableRow {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((header) => (
                  <TableHead
                    {...header.getHeaderProps({
                      style: {
                        maxWidth: header.maxWidth,
                        minWidth: header.minWidth,
                        width: header.width,
                      },
                    })}
                  >
                    <TableHeadContent
                      isSorted={header.isSorted}
                      isSortedDesc={header.isSortedDesc}
                      canSort={isSortable && header.canSort}
                      {...header.getSortByToggleProps?.()}
                    >
                      {header.render("Header")}
                    </TableHeadContent>
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody {...getTableBodyProps()}>
            {empty && data.length === 0 ? (
              <TableRow>
                <TableCell className="p-12" colSpan={columns.length}>
                  {empty}
                </TableCell>
              </TableRow>
            ) : (
              shownRows.map((row, i) => {
                prepareRow(row);

                return (
                  <TableRow
                    onClick={
                      typeof onRowClick === "function"
                        ? () => onRowClick(row.original)
                        : undefined
                    }
                    {...row.getRowProps()}
                  >
                    {row.cells.map((cell) => {
                      return (
                        <TableCell
                          {...cell.getCellProps({
                            style: {
                              maxWidth: cell.column.maxWidth,
                              minWidth: cell.column.minWidth,
                              width: cell.column.width,
                            },
                          })}
                        >
                          {cell.render("Cell")}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
        {paginate && (canPreviousPage || canNextPage) && (
          <Pagination
            canNextPage={canNextPage}
            canPreviousPage={canPreviousPage}
            gotoPage={gotoPage}
            pageCount={pageCount}
            pageIndex={pageIndex}
            className="mt-2"
          />
        )}
      </div>
      {isLoading ? <LoadingOverlay /> : null}
    </div>
  );
}
